'use client';

import { CraveErrorCode } from '@/enums/errorCodes';
import { fetchAPI } from '@/services/fetchAPI';
import type { AccountDetails, LoginResponse, Profile } from '@/types';
import {
  getSecureStorage,
  removeSecureStorage,
  setSecureStorage,
} from '@/utils/dataStorage';
import { isDev } from '@/utils/enviroment';
import { dispatchFatalError } from '@/utils/fatalError';

import { log } from '../appMonitoringSuite';

export interface StoredLoginInformation {
  accessToken: string;
  refreshToken: string;
  expires: number;
  scope: string;
  localization: string;
}

export enum StoredDataKeyName {
  USEREXPERIENCES = 'bell-crv-user-experiences-information',
  LOGININFORMATION = 'bell-crv-profile',
  SUBSCRIPTION = 'bell-crv-user-subscription-information',
  ULTRAHDENABLED = 'bell-crv-user-ultra-hd',
  USERACCOUNT = 'bell-crv-account',
  ACCOUNTDETAILS = 'bell-crv-account-details',
  EXPERIENCESKU = 'bell-crv-experience-sku',
}

export type StoredAccountResponse = {
  id: string;
  bdu?: boolean;
};

// Use proxy endpoints in development to avoid CORS issues
const API_BASE_URL = isDev
  ? '/api/proxy/bellmedia'
  : process.env.NEXT_PUBLIC_API_BASE_URL;
const API_USER = process.env.NEXT_PUBLIC_API_USER;
const API_PASSWORD = process.env.NEXT_PUBLIC_API_PASSWORD;
const base64Credentials = btoa(`${API_USER}:${API_PASSWORD}`);
const authHeader = `Basic ${base64Credentials}`;

// Function to calculate expiration time in seconds
export const nowInSeconds = () => Math.floor(Date.now() / 1000);

// Function to check if token has expired
const hasTokenExpired = (expires: number) => nowInSeconds() >= expires;

// Function to get stored profile from storage
export const getStoredProfile = (): StoredLoginInformation | undefined => {
  const profileData = getSecureStorage(StoredDataKeyName.LOGININFORMATION);
  if (!profileData) {
    return undefined;
  }

  try {
    return JSON.parse(profileData);
  } catch (error) {
    console.error('Failed to parse stored profile:', error);
    return undefined;
  }
};

// Function to get stored account from storage
export const getStoredAccount = (): StoredAccountResponse | null => {
  const accountData = getSecureStorage(StoredDataKeyName.USERACCOUNT);
  return accountData ? JSON.parse(accountData) : null;
};

// Function to get stored account details from storage
export const getStoredAccountDetails = (): AccountDetails | null => {
  const accountDetailsData = getSecureStorage(StoredDataKeyName.ACCOUNTDETAILS);
  return accountDetailsData ? JSON.parse(accountDetailsData) : null;
};

// Function to save profile to storage
export const saveProfile = (profile: StoredLoginInformation) => {
  const profileData = JSON.stringify(profile);
  setSecureStorage(StoredDataKeyName.LOGININFORMATION, profileData);
};

// Function to save account details to storage
export const saveAccountDetails = (accountDetails: AccountDetails) => {
  const accountDetailsData = JSON.stringify(accountDetails);
  setSecureStorage(StoredDataKeyName.ACCOUNTDETAILS, accountDetailsData);
};

// Function to save account to storage
export const saveAccount = (account: StoredAccountResponse) => {
  if (!account.id || account.id === '') {
    return;
  }

  const accountData = JSON.stringify(account);
  setSecureStorage(StoredDataKeyName.USERACCOUNT, accountData);
};

/**
 * Which profile a session may land on with no user input: only ever the account's
 * single profile. An account with more than one profile picks again on every
 * launch, so this returns null and the caller shows the picker - the last pick is
 * deliberately not consulted.
 *
 * Callers must check hasPin on the result - a PIN-protected profile is only ever
 * entered through /profile/enter-pin, which is also what scopes the token to it.
 */
export const resolveAutoSelectedProfile = (profiles: Profile[]): Profile | null =>
  profiles.length === 1 ? profiles[0] : null;

// Function to remove the stored profile (logout) from storage
export const removeProfile = () => {
  removeSecureStorage(StoredDataKeyName.LOGININFORMATION);
  removeSecureStorage(StoredDataKeyName.USERACCOUNT);
  removeSecureStorage(StoredDataKeyName.ACCOUNTDETAILS);

  const localOnlyKeys = [
    StoredDataKeyName.USEREXPERIENCES,
    StoredDataKeyName.EXPERIENCESKU,
    StoredDataKeyName.SUBSCRIPTION,
    StoredDataKeyName.ULTRAHDENABLED,
  ];

  localOnlyKeys.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove localStorage item for ${key}:`, error);
    }
  });
};

// Function to check if an email exists
export const emailExists = async (email: string): Promise<boolean> => {
  const url = `${API_BASE_URL}/account/v1.2/email-exists`;
  const options = {
    method: 'HEAD',
    headers: {
      accept: 'application/json',
      email: email,
    },
  };

  try {
    const response = await fetchAPI(url, options);
    return response;
  } catch (error) {
    throw error; // Handle other errors
  }
};

// Function to send recovery password email
export const sendRecoveryPasswordEmail = async (email: string): Promise<void> => {
  const url = `${API_BASE_URL}/account/v1.1/send-recovery-password-email?email=${encodeURIComponent(email)}`;
  const options = {
    method: 'POST',
    headers: {
      accept: 'application/json',
    },
  };

  try {
    const response = await fetchAPI(url, options);
    if (response.status === 200) {
      return;
    } else if (response.status === 404) {
      throw new Error('404');
    } else if (response.status === 403) {
      throw new Error('403');
    } else {
      throw new Error('Failed to send recovery email');
    }
  } catch (error: any) {
    // Re-throw with original error message if it's a known status code
    if (error.message === '404' || error.message === '403') {
      throw error;
    }
    // For network errors or other issues, throw a generic error
    throw new Error('Failed to send recovery email');
  }
};

// Function to handle login
export const login = async (
  username: string,
  password: string,
  apiVersion: string = 'v2.2'
): Promise<LoginResponse> => {
  const url = `${API_BASE_URL}/login/${apiVersion}`;
  const formData = new URLSearchParams();
  formData.append('grant_type', 'password');
  formData.append('username', username);
  formData.append('password', password);

  const options = {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      authorization: authHeader,
    },
    body: formData.toString(),
  };

  try {
    const response = await fetchAPI(url, options);
    if (response.status === 200) {
      const data = await response?.json();
      const accessToken = data.access_token;
      const refreshToken = data.refresh_token;
      const expires = nowInSeconds() + data.expires_in; // Calculate expiration time in seconds
      const scope = data.scope;
      const localization = data.localization;
      // Save profile to localStorage
      saveProfile({ accessToken, refreshToken, expires, scope, localization });
      return data;
    }
    throw new Error(response.status.toString());
  } catch (error) {
    throw error; // Error handling for failed login
  }
};

// Function to fetch account details using the access token
export const getAccountDetails = async (accessToken: string): Promise<AccountDetails> => {
  const url = `${API_BASE_URL}/account/v1.1`;
  const options = {
    method: 'GET',
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${accessToken}`, // Use the access token to authorize the request
    },
  };

  const response = await fetchAPI(url, options);
  if (response) {
    const data = await response.json();
    saveAccount({
      id: data.id,
      bdu: data.bdu,
    });
    // Save full account details to storage
    saveAccountDetails(data);
    return data;
  }
  throw new Error('Failed to fetch account details');
};

// Function to fetch profiles
export const getProfiles = async (
  accessToken: string | null,
  apiVersion: string = 'v2'
): Promise<Profile[]> => {
  if (!accessToken) {
    throw new Error('Access token is missing');
  }

  const account = await getAccountDetails(accessToken);
  if (!account) {
    throw new Error('Account not found');
  }

  const url = `${API_BASE_URL}/profile/${apiVersion}/account/${account.id}`;

  const options = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  };

  try {
    if (!accessToken) {
      throw new Error('Access token is missing');
    }
    const response = await fetchAPI(url, options);
    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch profiles:', error);
    throw new Error('Could not retrieve profiles');
  }
};

export const addProfile = async (
  accessToken: string | null,
  profile: Partial<Profile>,
  apiVersion: string = 'v2'
): Promise<Profile> => {
  if (!accessToken) {
    throw new Error('Access token is missing');
  }

  const account = await getAccountDetails(accessToken);
  if (!account) {
    throw new Error('Account not found');
  }

  const url = `${API_BASE_URL}/profile/${apiVersion}/account/${account.id}`;

  const options = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(profile),
  };

  try {
    const response = await fetchAPI(url, options);
    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to add profile:', error);
    throw new Error('Could not add profile');
  }
};

// Update user profile
export const updateProfile = async (
  accessToken: string | null,
  profile: Partial<Profile>,
  apiVersion: string = 'v2'
): Promise<Profile> => {
  if (!accessToken) {
    throw new Error('Access token is missing');
  }

  const url = `${API_BASE_URL}/profile/${apiVersion}/${profile.id}`;

  const options = {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(profile),
  };

  try {
    const response = await fetchAPI(url, options);
    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to update profile:', error);
    throw new Error('Could not update profile');
  }
};

export const setUserPlaybackLanguages = async (
  accessToken: string | null,
  profileId: string,
  playbackLanguages: string[],
  apiVersion: string = 'v2'
): Promise<void> => {
  const url = `${API_BASE_URL}/profile/${apiVersion}/${profileId}`;
  const options = {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ playbackLanguages }),
  };
  try {
    if (!accessToken) {
      throw new Error('Access token is missing');
    }
    const response = await fetchAPI(url, options);
    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    return;
  } catch (error) {
    console.error('Failed to set user playback languages:', error);
    throw new Error('Could not set user playback languages');
  }
};

export const setUserOnboarded = async (
  accessToken: string | null,
  profileId: string,
  apiVersion: string = 'v2'
): Promise<void> => {
  const url = `${API_BASE_URL}/profile/${apiVersion}/${profileId}/onboarded`;
  const options = {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  };
  try {
    if (!accessToken) {
      throw new Error('Access token is missing');
    }
    const response = await fetchAPI(url, options);
    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    return response;
  } catch (error) {
    console.error('Failed to set user onboarded:', error);
    throw new Error('Could not set user onboarded');
  }
};

//delete profile
export const deleteProfile = async (
  accessToken: string | null,
  profileId: string,
  apiVersion: string = 'v1.1'
): Promise<Profile | null> => {
  if (!accessToken) {
    throw new Error('Access token is missing');
  }

  const url = `${API_BASE_URL}/profile/${apiVersion}/${profileId}`;

  const options = {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  };

  try {
    const response = await fetchAPI(url, options);

    if (response.ok) {
      return response;
    }
    throw new Error(`Error: ${response.status} ${response.statusText}`);
  } catch (error) {
    console.error('Failed to delete profile:', error);
    throw new Error('Could not delete profile');
  }
};

// Function to regenerate access token using the refresh token
export const refreshToken = async (
  profileID?: string,
  profilePin?: string,
  apiVersion: string = 'v2.2'
): Promise<LoginResponse> => {
  const storedProfile = getStoredProfile();
  if (!storedProfile || !storedProfile.refreshToken) {
    throw new Error('No refresh token available');
  }
  const url = `${API_BASE_URL}/login/${apiVersion}`;

  const formData = new URLSearchParams();
  formData.append('grant_type', 'refresh_token');
  formData.append('refresh_token', storedProfile.refreshToken);
  if (profileID) formData.append('profile_id', profileID);
  if (profilePin) formData.append('profile_pin', profilePin);

  const options = {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      authorization: authHeader,
    },
    body: formData.toString(),
  };

  try {
    const response = await fetchAPI(url, options);
    if (response.status === 200) {
      const data = await response?.json();
      const accessToken = data.access_token;
      const refreshToken = data.refresh_token;
      const expires = nowInSeconds() + data.expires_in;
      const scope = data.scope;
      const localization = data.localization;
      // Save the new profile with updated tokens and expiration time
      saveProfile({ accessToken, refreshToken, expires, scope, localization });
      return data;
    }
    log('Auth-Service: Refresh Token Fetch failed after success', 'error');
    throw new Error('Failed to refresh token');
  } catch (error) {
    log('Auth-Service: Refresh Token Fetch failed', 'error');
    // A 429 is a transient rate limit; the session is still valid. Surface the
    // generic error screen rather than letting callers sign the user out.
    // Centralised here so every refresh path is covered.
    if (error instanceof Error && error.message === '429') {
      dispatchFatalError(CraveErrorCode.CONNECTION_ERROR_4003);
    }
    throw error;
  }
};

// Function to validate PIN for a profile
export const enterPin = async (
  profileId: string,
  profilePin: string,
  apiVersion: string = 'v2.2',
  isEditMode?: boolean
): Promise<string | undefined> => {
  const storedProfile = getStoredProfile();
  if (!storedProfile || !storedProfile.refreshToken) {
    throw new Error('No refresh token available');
  }

  const url = `${API_BASE_URL}/login/${apiVersion}`;

  const formData = new URLSearchParams();
  formData.append('grant_type', 'refresh_token');
  formData.append('refresh_token', storedProfile.refreshToken);
  formData.append('profile_id', profileId);
  formData.append('profile_pin', profilePin);

  const options = {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      authorization: authHeader,
    },
    body: formData.toString(),
  };

  try {
    const response = await fetchAPI(url, options);
    if (response.status === 200) {
      const data: LoginResponse = await response.json();

      if (!isEditMode) {
        saveProfile({
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          expires: nowInSeconds() + data.expires_in,
          scope: data.scope,
          localization: data.localization,
        });
      }

      return data.access_token;
    } else {
      return Promise.reject(new Error(response.status.toString()));
    }
  } catch (error: any) {
    console.error('Failed to validate PIN:', error);
    throw new Error(error.message);
  }
};

// Function to get the access token (checks expiration, refreshes if necessary)
export const getAccessToken = async (): Promise<string | null> => {
  const storedProfile = getStoredProfile();
  if (!storedProfile) {
    return null;
  }

  if (hasTokenExpired(storedProfile.expires)) {
    try {
      const refreshedProfile = await refreshToken();
      return refreshedProfile.access_token;
    } catch (error) {
      console.error('Failed to refresh access token:', error);
      return null;
    }
  }

  return storedProfile.accessToken;
};

export const getProfileById = async (
  id: string,
  accessToken: string | null,
  apiVersion: string = 'v1.1'
): Promise<Profile> => {
  try {
    if (!id) {
      throw new Error('Id is missing');
    }

    if (!accessToken) {
      throw new Error('Access token is missing');
    }
    const response = await fetchAPI(`${API_BASE_URL}/profile/${apiVersion}/${id}`, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch profile:', error);
    throw new Error('Could not retrieve profile');
  }
};

// Function to handle logout
export const logout = () => {
  removeProfile(); // Clear the profile from localStorage
};
